import { useEffect, useState } from "react";
import { toast } from "sonner";
import { apiCreate, apiDelete, apiList, apiUpdate } from "@/lib/api";
import { useActiveClinicaId } from "@/hooks/useActiveClinicaId";
import { useAuth } from "@/contexts/AuthContext";

export interface ModeloAtestado {
  id: string;
  titulo: string;
  tipo: string;
  conteudo: string;
  ativo: boolean;
  created_at?: string;
  updated_at?: string;
}

export interface DocumentoEmitido {
  id: string;
  clinica_id?: string;
  clinica_dados?: Record<string, unknown>;
  paciente_id: string;
  modelo_id?: string;
  tipo: string;
  titulo: string;
  conteudo: string;
  profissional?: string;
  cro?: string;
  data_emissao: string;
  status: "Emitido" | "Cancelado";
  created_at?: string;
  updated_at?: string;
}

export type ModeloAtestadoInput = Omit<ModeloAtestado, "id" | "created_at" | "updated_at">;
export type DocumentoEmitidoInput = Omit<DocumentoEmitido, "id" | "created_at" | "updated_at">;

export const modelosAtestadoPadrao: ModeloAtestadoInput[] = [
  {
    titulo: "Modelo Sorriibem - Atestado Odontológico",
    tipo: "Atestado",
    ativo: true,
    conteudo:
      "Atestamos, para os devidos fins, que o(a) Sr(a). {paciente}\nCPF nº {cpf}\nesteve sob tratamento odontológico neste consultório no período das {hora_inicio} às {hora_fim} horas do dia {data}, necessitando de {dias_repouso} de convalescença.\nC.I.D. {cid}\nObs.: {observacoes_atestado}",
  },
  {
    titulo: "Atestado de comparecimento",
    tipo: "Comparecimento",
    ativo: true,
    conteudo:
      "Atesto, para os devidos fins, que {paciente} compareceu a esta clínica em {data}, permanecendo em atendimento pelo período de {periodo}.",
  },
  {
    titulo: "Atestado de repouso",
    tipo: "Repouso",
    ativo: true,
    conteudo:
      "Atesto que {paciente} necessita de repouso por {dias_repouso} dia(s), a contar de {data}, em decorrência de procedimento odontológico realizado.",
  },
  {
    titulo: "Declaração de tratamento odontológico",
    tipo: "Tratamento",
    ativo: true,
    conteudo:
      "Declaramos que {paciente} encontra-se em tratamento odontológico nesta clínica, com acompanhamento profissional conforme planejamento clínico registrado.",
  },
  {
    titulo: "Atestado para acompanhante",
    tipo: "Acompanhante",
    ativo: true,
    conteudo:
      "Atesto que o acompanhante de {paciente} esteve presente nesta clínica em {data}, durante o atendimento odontológico do paciente.",
  },
  {
    titulo: "Encaminhamento odontológico",
    tipo: "Encaminhamento",
    ativo: true,
    conteudo:
      "Encaminho {paciente} para avaliação complementar, conforme necessidade clínica observada durante o atendimento odontológico.",
  },
  {
    titulo: "Declaração de retorno",
    tipo: "Retorno",
    ativo: true,
    conteudo:
      "Declaramos que {paciente} possui retorno odontológico agendado para continuidade do tratamento, conforme orientação profissional.",
  },
];

function normalizarTituloModelo(titulo: string) {
  return titulo
    .trim()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLocaleLowerCase("pt-BR")
    .replace(/\s+/g, " ");
}

export function preencherModeloAtestado(
  conteudo: string,
  valores: Record<string, string>
) {
  return Object.entries(valores).reduce(
    (texto, [chave, valor]) => texto.replace(new RegExp(`\\{${chave}\\}`, "g"), valor || "-"),
    conteudo
  );
}

export function useAtestados() {
  const { user } = useAuth();
  const activeClinicaId = useActiveClinicaId();
  const [modelos, setModelos] = useState<ModeloAtestado[]>([]);
  const [documentos, setDocumentos] = useState<DocumentoEmitido[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchAll = async () => {
    if (!user) {
      setModelos([]);
      setDocumentos([]);
      return;
    }
    try {
      setLoading(true);
      const [modelosResult, docsResult] = await Promise.allSettled([
        apiList<ModeloAtestado>("modelos_atestado"),
        apiList<DocumentoEmitido>("documentos_emitidos"),
      ]);

      if (modelosResult.status === "fulfilled") {
        setModelos(modelosResult.value.sort((a, b) => a.titulo.localeCompare(b.titulo)));
      } else {
        setModelos([]);
        toast.error("Erro ao carregar modelos de atestado: " + modelosResult.reason.message);
      }

      if (docsResult.status === "fulfilled") {
        setDocumentos(docsResult.value.sort((a, b) => String(b.data_emissao || "").localeCompare(String(a.data_emissao || ""))));
      } else {
        setDocumentos([]);
        toast.error("Erro ao carregar atestados emitidos: " + docsResult.reason.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const seedModelosPadrao = async () => {
    try {
      const modelosAtuais = await apiList<ModeloAtestado>("modelos_atestado");
      const titulosExistentes = new Set(modelosAtuais.map((modelo) => normalizarTituloModelo(modelo.titulo)));
      const modelosPendentes = modelosAtestadoPadrao.filter((modelo) => !titulosExistentes.has(normalizarTituloModelo(modelo.titulo)));

      if (modelosPendentes.length === 0) {
        toast.info("Todos os modelos padrão já existem.");
        await fetchAll();
        return;
      }

      await Promise.all(modelosPendentes.map((modelo) => apiCreate<ModeloAtestado>("modelos_atestado", modelo as unknown as Record<string, unknown>)));
      await fetchAll();
      toast.success(`${modelosPendentes.length} modelo(s) padrão criado(s). Os modelos existentes foram mantidos.`);
    } catch (error: any) {
      toast.error("Erro ao criar modelos: " + error.message);
    }
  };

  const createModelo = async (input: ModeloAtestadoInput) => {
    const modelosAtuais = await apiList<ModeloAtestado>("modelos_atestado");
    const existe = modelosAtuais.some((modelo) => normalizarTituloModelo(modelo.titulo) === normalizarTituloModelo(input.titulo));
    if (existe) {
      toast.error("Já existe um modelo com este título.");
      return null;
    }
    const data = await apiCreate<ModeloAtestado>("modelos_atestado", input as unknown as Record<string, unknown>);
    await fetchAll();
    toast.success("Modelo salvo.");
    return data;
  };

  const updateModelo = async (id: string, input: Partial<ModeloAtestadoInput>) => {
    if (input.titulo) {
      const modelosAtuais = await apiList<ModeloAtestado>("modelos_atestado");
      const existe = modelosAtuais.some((modelo) => modelo.id !== id && normalizarTituloModelo(modelo.titulo) === normalizarTituloModelo(input.titulo || ""));
      if (existe) {
        toast.error("Já existe um modelo com este título.");
        return null;
      }
    }
    const data = await apiUpdate<ModeloAtestado>("modelos_atestado", id, input as unknown as Record<string, unknown>);
    await fetchAll();
    toast.success("Modelo atualizado.");
    return data;
  };

  const deleteModelo = async (id: string) => {
    await apiDelete("modelos_atestado", id);
    await fetchAll();
    toast.success("Modelo removido.");
  };

  const createDocumento = async (input: DocumentoEmitidoInput) => {
    const data = await apiCreate<DocumentoEmitido>("documentos_emitidos", input as unknown as Record<string, unknown>);
    await fetchAll();
    toast.success("Documento emitido.");
    return data;
  };

  const updateDocumento = async (id: string, input: Partial<DocumentoEmitidoInput>) => {
    const data = await apiUpdate<DocumentoEmitido>("documentos_emitidos", id, input as unknown as Record<string, unknown>);
    await fetchAll();
    toast.success("Documento atualizado.");
    return data;
  };

  const deleteDocumento = async (id: string) => {
    await apiDelete("documentos_emitidos", id);
    await fetchAll();
    toast.success("Documento removido.");
  };

  useEffect(() => {
    fetchAll();
  }, [user?.id, activeClinicaId]);

  return {
    modelos,
    documentos,
    loading,
    fetchAll,
    seedModelosPadrao,
    createModelo,
    updateModelo,
    deleteModelo,
    createDocumento,
    updateDocumento,
    deleteDocumento,
  };
}
